"use client";
import { fileUploadAction } from "@/actions/file";
import { assertUnreachable, cn, invariant } from "@/lib/utils";
import { SingleImageInput } from "@/ui/image-upload/single-image-input";
import { Loader2Icon, UploadIcon, XIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { useOptimistic, useRef, useTransition } from "react";

type FileUploadState =
	| {
			id: string;
			status: "UPLOADING_NO_PREVIEW";
	  }
	| {
			id: string;
			previewUrl: string;
			status: "UPLOADING_WITH_PREVIEW";
	  }
	| {
			id: string;
			status: "ERROR";
			message: string;
			previewUrl?: string;
	  }
	| {
			id: string;
			status: "SUCCESS_LOADING";
			url: string;
	  }
	| {
			id: string;
			status: "SUCCESS_LOADED";
			url: string;
			width?: number;
			height?: number;
	  };

type OptimisticImagesState = FileUploadState[];
type OptimisticImagesAction =
	| { type: "UPLOAD_START"; id: string }
	| { type: "UPLOAD_SUCCESS"; id: string; url: string; width: number; height: number }
	| { type: "UPLOAD_ERROR"; id: string; errorMessage: string }
	| { type: "FILE_READ_SUCCESS"; id: string; previewUrl: string }
	| { type: "FILE_READ_ERROR"; id: string; errorMessage: string }
	| { type: "DIMENSIONS_LOADED"; id: string; width: number; height: number }
	| { type: "IMAGE_LOADED"; id: string; url: string }
	| { type: "DELETE_FILE"; id: string; url?: string };

export const ImageUpload = ({
	name,
	images,
	onUpload,
	onDelete,
	maxImages,
	className,
	width,
	height,
}: {
	name?: string;
	images: (string | undefined)[];
	onUpload: (imageUrl: string, { width, height }: { width: number; height: number }) => void;
	onDelete: (imageUrl: string) => void;
	maxImages: number;
	className?: string;
	width?: number;
	height?: number;
}) => {
	const [_isTransitioning, startTransition] = useTransition();

	const aspectRatio = width && height ? `${width} / ${height}` : `1`;

	/**
	 * This is a hack to keep the result of the file reader.
	 *
	 * We need to keep the preview image in memory to avoid flickering after
	 * the image is uploaded to the server but not yet downloaded by the browser.
	 * @see https://x.com/rauchg/status/1805274417158824431
	 */
	const urlTofileReaderResult = useRef<Map<string, string>>(new Map());

	const [optimisticImages, dispatchOptimisticImagesAction] = useOptimistic<
		OptimisticImagesState,
		OptimisticImagesAction
	>(
		images.filter(Boolean).map(
			(imageUrl): FileUploadState => ({
				id: imageUrl,
				status: "SUCCESS_LOADING",
				url: imageUrl,
			}),
		),
		(state, action) => {
			switch (action.type) {
				case "UPLOAD_START":
					return [...state, { id: action.id, status: "UPLOADING_NO_PREVIEW" }];
				case "UPLOAD_SUCCESS":
					return state.map((upload) => {
						if (upload.id === action.id && upload.status === "UPLOADING_WITH_PREVIEW") {
							urlTofileReaderResult.current.set(action.url, upload.previewUrl);
						}

						return upload.id === action.id
							? {
									id: action.id,
									status: "SUCCESS_LOADING",
									url: action.url,
								}
							: upload;
					});
				case "IMAGE_LOADED":
					return state.map((upload) =>
						upload.id === action.id
							? {
									...upload,
									id: action.id,
									status: "SUCCESS_LOADED",
									url: action.url,
								}
							: upload,
					);
				case "UPLOAD_ERROR":
					return state.map((upload) =>
						upload.id === action.id
							? { id: action.id, status: "ERROR", message: action.errorMessage }
							: upload,
					);
				case "FILE_READ_SUCCESS":
					return state.map((upload) =>
						upload.id === action.id && upload.status === "UPLOADING_NO_PREVIEW"
							? {
									id: action.id,
									previewUrl: action.previewUrl,
									status: "UPLOADING_WITH_PREVIEW",
								}
							: upload,
					);
				case "FILE_READ_ERROR":
					return state.map((upload) =>
						upload.id === action.id
							? { id: action.id, status: "ERROR", message: action.errorMessage }
							: upload,
					);
				case "DIMENSIONS_LOADED":
					return state.map((upload) =>
						upload.id === action.id
							? {
									...upload,
									width: action.width,
									height: action.height,
								}
							: upload,
					);
				case "DELETE_FILE":
					return state.filter((upload) => upload.id !== action.id);
			}
			return assertUnreachable(action);
		},
	);

	const optimisticImagesDeduplicated = optimisticImages.reduceRight<OptimisticImagesState>((acc, item) => {
		if (!("url" in item)) {
			console.log(item);
			acc.unshift(item);
		} else if (!acc.some((existing) => "url" in existing && existing.url === item.url)) {
			console.log(item);
			acc.unshift(item);
		}
		return acc;
	}, []);

	const optimisticImagesRef = useRef<OptimisticImagesState>([]);
	optimisticImagesRef.current = optimisticImagesDeduplicated;

	const totalElements = optimisticImagesDeduplicated.length;
	const allowUploadingMore = totalElements < maxImages;
	const placeholderElements = (3 - (totalElements % 3)) % 3;

	const onFileUpload = (fileList: FileList | null) => {
		if (!fileList) {
			return;
		}
		const files = Array.from(fileList).filter((file) => file.type.startsWith("image/"));
		if (files.length === 0) {
			return;
		}
		files.map((file) => {
			const id = nanoid();
			startTransition(() => dispatchOptimisticImagesAction({ id, type: "UPLOAD_START" }));

			uploadImage({ id, file })
				.then((url) => {
					startTransition(() => {
						const currentUpload = optimisticImagesRef.current.find((upload) => upload.id === id);
						const { width, height } =
							currentUpload && "width" in currentUpload ? currentUpload : { width: 0, height: 0 };
						invariant(width !== undefined && height !== undefined, "Width and height must be greater than 0");

						dispatchOptimisticImagesAction({ id, type: "UPLOAD_SUCCESS", url, width, height });
						return onUpload(url, { width, height });
					});
				})
				.catch((error) => {
					console.error(error);
					const errorMessage = error instanceof Error ? error.name + " " + error.message : "Error";
					startTransition(() => dispatchOptimisticImagesAction({ id, type: "UPLOAD_ERROR", errorMessage }));
				});

			readFileAsync(file)
				.then((previewUrl) => {
					startTransition(() =>
						dispatchOptimisticImagesAction({ id, type: "FILE_READ_SUCCESS", previewUrl }),
					);
				})
				.catch((error) => {
					const errorMessage = error instanceof Error ? error.name + " " + error.message : "Error";
					startTransition(() =>
						dispatchOptimisticImagesAction({ id, type: "FILE_READ_ERROR", errorMessage }),
					);
				});
		});
	};

	return (
		<div data-yns-click className={cn("grid gap-2", className)}>
			<div className="grid grid-cols-3 gap-2 [&>*:first-child]:col-span-3">
				{optimisticImagesDeduplicated.map((upload, idx) => {
					const isFirst = idx === 0;
					switch (upload.status) {
						case "UPLOADING_NO_PREVIEW":
							return (
								<div
									key={upload.id}
									className="flex w-full cursor-pointer items-center justify-center rounded-md border transition-colors hover:bg-neutral-50"
									style={{ aspectRatio }}
								>
									<Loader2Icon className="h-3 w-3 animate-spin" />
								</div>
							);
						case "UPLOADING_WITH_PREVIEW":
							return (
								<SingleImageInput
									name={name}
									key={upload.id}
									image={upload.previewUrl}
									size={isFirst ? "large" : "small"}
									width={width}
									height={height}
									isUploading={true}
									onLoad={(e) => {
										const { naturalWidth, naturalHeight } = e.currentTarget;
										startTransition(() =>
											dispatchOptimisticImagesAction({
												type: "DIMENSIONS_LOADED",
												id: upload.id,
												width: naturalWidth,
												height: naturalHeight,
											}),
										);
									}}
									onDelete={() => {
										startTransition(() =>
											dispatchOptimisticImagesAction({ type: "DELETE_FILE", id: upload.id }),
										);
									}}
								/>
							);
						case "SUCCESS_LOADING":
						case "SUCCESS_LOADED":
							return (
								<SingleImageInput
									name={name}
									key={upload.id}
									image={
										upload.status === "SUCCESS_LOADED"
											? upload.url
											: (urlTofileReaderResult.current.get(upload.url) ?? upload.url)
									}
									preload={urlTofileReaderResult.current.has(upload.url) ? upload.url : undefined}
									size={isFirst ? "large" : "small"}
									width={width}
									height={height}
									onLoad={(e) => {
										urlTofileReaderResult.current.delete(upload.url);
										startTransition(() =>
											dispatchOptimisticImagesAction({
												type: "IMAGE_LOADED",
												id: upload.id,
												url: upload.url,
											}),
										);
									}}
									onDelete={() => {
										startTransition(() => {
											dispatchOptimisticImagesAction({
												type: "DELETE_FILE",
												id: upload.id,
												url: upload.url,
											});
											return onDelete(upload.url);
										});
									}}
								/>
							);
						case "ERROR":
							return (
								<div
									key={upload.id}
									className="flex w-full cursor-pointer items-center justify-center rounded-md border transition-colors hover:bg-neutral-50"
									style={{ aspectRatio }}
								>
									<XIcon className="h-6 w-6 text-destructive-foreground" />
								</div>
							);
					}
					return assertUnreachable(upload);
				})}

				{allowUploadingMore &&
					Array(placeholderElements)
						.fill(0)
						.map((_, i) => <div key={i} />)}

				{allowUploadingMore && (
					<label
						className="relative flex w-full transition-[background] group cursor-pointer items-center justify-center rounded-md border border-dashed hover:bg-neutral-50 bg-white p-8 shadow-xs in-data-[is-dragging=true]:border-neutral-500 in-data-[is-dragging=true]:border-2 in-data-[is-dragging=true]:border-solid mb-1"
						style={{ aspectRatio }}
					>
						<span className="sr-only">Upload image</span>
						<UploadIcon className="h-4 w-4 text-neutral-400 group-hover:text-neutral-600 in-data-[is-dragging=true]:text-neutral-600 transition-colors" />
						<input
							key={optimisticImagesDeduplicated.length}
							type="file"
							className="absolute inset-0 z-10 cursor-pointer opacity-0"
							accept="image/*"
							multiple
							onDragOver={(e) => {
								e.stopPropagation();
								e.preventDefault();
								e.dataTransfer.dropEffect = "copy";
							}}
							onDrop={(e) => {
								e.preventDefault();
								onFileUpload(e.dataTransfer.files);
							}}
							onChange={(e) => {
								e.preventDefault();
								onFileUpload(e.target.files);
							}}
						/>
					</label>
				)}
			</div>
		</div>
	);
};

const readFileAsync = (file: File) => {
	const { promise, reject, resolve } = Promise.withResolvers<string>();
	const reader = new FileReader();
	reader.addEventListener("load", () => resolve(reader.result?.toString() ?? ""));
	reader.addEventListener("error", () => reject(reader.error));
	reader.readAsDataURL(file);
	return promise;
};

export async function uploadImage({ id, file }: { id: string; file: File }) {
	const formData = new FormData();
	formData.append("id", id);
	formData.append("image", file);

	const result = await fileUploadAction(formData);
	if (result?.serverError) {
		throw new Error(result.serverError);
	}
	if (result?.validationErrors) {
		throw new Error("Validation error", { cause: result?.validationErrors });
	}
	if (result?.bindArgsValidationErrors) {
		throw new Error("Validation error", { cause: result?.bindArgsValidationErrors });
	}
	if (!result?.data) {
		throw new Error("No data returned");
	}
	const url = result.data.url;
	return url;
}
